/**
 * The trust pages (web plan W3.7): About, How we verify deals, For
 * businesses and Contact — global, un-prefixed pages (master plan D-2) whose
 * copy is owner content (@/content/trust).
 *
 * A page is **live** once the owner has written its copy (and, for Contact,
 * set the contact email, @/content/site). Only a live page exists: it is in
 * the shared route list (@/routes/legacyRoutes), so the SPA renders it and the
 * SEO server answers 200 with its head (`trustPageHead` in ./pageHeads), and
 * the footer links it. A page that is not live is not a route at all: its URL
 * is the normal "Page not found" (`noindex`, 404 from the SEO server) and
 * nothing links it — nothing fake ever goes live.
 *
 * Pure and light (no `window`, no React, no templates): the footer and the
 * route list, both in the entry chunk, import it. The head builder lives in
 * ./pageHeads with the other page heads.
 */
import { SITE_CONTACT_EMAIL } from '@/content/site';
import { TRUST_COPY, type TrustCopy, type TrustPageKey } from '@/content/trust';

export type { TrustCopy, TrustPageKey };

export interface TrustPage {
  key: TrustPageKey;
  /** The page's URL (global, lower case, never under a locale prefix). */
  path: string;
}

/** The trust pages, in the footer's order. */
export const TRUST_PAGES: readonly TrustPage[] = Object.freeze([
  { key: 'about', path: '/about' },
  { key: 'howWeVerify', path: '/how-we-verify' },
  { key: 'forBusinesses', path: '/for-businesses' },
  { key: 'contact', path: '/contact' },
] satisfies TrustPage[]);

/** What decides whether (and what) a trust page shows. */
export interface TrustContent {
  copy: Readonly<Record<TrustPageKey, TrustCopy>>;
  contactEmail: string;
}

/** The owner's content: what the site serves. Tests pass their own fixture instead. */
export const OWNER_TRUST_CONTENT: TrustContent = Object.freeze({
  copy: TRUST_COPY,
  contactEmail: SITE_CONTACT_EMAIL,
});

/** The path of a trust page. */
export function trustPagePath(key: TrustPageKey): string {
  const page = TRUST_PAGES.find((candidate) => candidate.key === key);
  if (!page) throw new RangeError(`trustPagePath: unknown trust page ${key}`);
  return page.path;
}

/** One piece of a page's copy: a subheading (`## …`) or a paragraph. */
export interface TrustBlock {
  type: 'heading' | 'paragraph';
  text: string;
}

/**
 * The copy as blocks: paragraphs split on blank lines, whitespace collapsed,
 * empties dropped; a paragraph starting with "## " is a subheading.
 */
export function trustBlocks(body: string | null | undefined): TrustBlock[] {
  return (body ?? '')
    .split(/\n\s*\n/)
    .map((part) => part.replace(/\s+/g, ' ').trim())
    .map((text): TrustBlock =>
      /^##\s/.test(text) ? { type: 'heading', text: text.replace(/^##\s+/, '').trim() } : { type: 'paragraph', text },
    )
    .filter((block) => block.text !== '');
}

// A plain address (`hello@promostork.com`): nothing that would add to or break
// a `mailto:` link (spaces, `?subject=`, `<>`, quotes, a second "@").
const EMAIL = /^[A-Za-z0-9._%+-]+@[A-Za-z0-9-]+(?:\.[A-Za-z0-9-]+)*\.[A-Za-z]{2,}$/;

/** The contact address when it is a usable one (trimmed), else null. */
export function contactEmailOf(content: TrustContent = OWNER_TRUST_CONTENT): string | null {
  const email = content.contactEmail.trim();
  return EMAIL.test(email) ? email : null;
}

/**
 * Whether a trust page is live: its copy has at least one paragraph (a
 * subheading alone is not a page), and Contact also has a contact address.
 */
export function isTrustPageLive(key: TrustPageKey, content: TrustContent = OWNER_TRUST_CONTENT): boolean {
  const blocks = trustBlocks(content.copy[key]?.body);
  if (!blocks.some((block) => block.type === 'paragraph')) return false;
  return key !== 'contact' || contactEmailOf(content) !== null;
}

/** The live trust pages, in the footer's order. */
export function liveTrustPages(content: TrustContent = OWNER_TRUST_CONTENT): TrustPage[] {
  return TRUST_PAGES.filter((page) => isTrustPageLive(page.key, content));
}

/**
 * A page's meta description before the head's clean-up (hashtags and URLs
 * stripped, at most 160 characters): the owner's `description`, else the
 * copy's first paragraph.
 */
export function trustDescription(copy: TrustCopy | null | undefined): string {
  const own = copy?.description?.replace(/\s+/g, ' ').trim();
  if (own) return own;
  return trustBlocks(copy?.body).find((block) => block.type === 'paragraph')?.text ?? '';
}
